import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { cloutReviewsApi, PublishedReview, ReviewInput } from '../../services/cloutReviews';
import ReviewForm from './ReviewForm';

const PAGE_SIZE = 10;

export const KIND_LABELS: Record<PublishedReview['kind'], string> = {
  invited: 'Invited by the provider',
  member: 'CLOUT member',
};

const Stars: React.FC<{ rating: number }> = ({ rating }) => (
  <span className="text-[#c6a664]" aria-label={`${rating} out of 5 stars`}>
    {'★'.repeat(rating)}
    <span className="text-[#d9d6d0]">{'★'.repeat(5 - rating)}</span>
  </span>
);

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

interface ProviderReviewsProps {
  providerId: number;
  providerName: string;
  isOwnProfile?: boolean;
}

// Approved reviews clients left on CLOUT, and a form for signed-in members to add one
const ProviderReviews: React.FC<ProviderReviewsProps> = ({
  providerId,
  providerName,
  isOwnProfile = false,
}) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<PublishedReview[]>([]);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [writing, setWriting] = useState(false);
  const [submittedMessage, setSubmittedMessage] = useState<string | null>(null);

  const loadPage = useCallback(
    async (skip: number) => {
      try {
        const page = await cloutReviewsApi.listPublished(providerId, skip, PAGE_SIZE);
        setReviews((current) => (skip === 0 ? page.reviews : [...current, ...page.reviews]));
        setTotal(page.total);
      } catch {
        // Leave the list as it is; the section still offers to write a review
      } finally {
        setLoaded(true);
      }
    },
    [providerId]
  );

  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  const handleSubmit = async (review: ReviewInput) => {
    const result = await cloutReviewsApi.submitAsMember(providerId, review);
    setWriting(false);
    setSubmittedMessage(result.message);
  };

  const renderWriteAction = () => {
    if (isOwnProfile || submittedMessage) return null;
    if (!user) {
      return (
        <Link to="/signin" className="text-[#c6a664] hover:underline">
          Sign in to write a review
        </Link>
      );
    }
    // Provider accounts endorse peers instead; the API refuses their reviews too
    if (user.role === 'provider' || user.role === 'admin') return null;
    if (user.email_verified === false) {
      return (
        <span className="text-[#767472]">Verify your email address to write a review.</span>
      );
    }
    if (writing) return null;
    return (
      <button
        type="button"
        onClick={() => setWriting(true)}
        className="px-4 py-2 border border-[#1f1f1f] text-[#1f1f1f] text-xs font-semibold uppercase hover:bg-[#1f1f1f] hover:text-white"
      >
        Write a review
      </button>
    );
  };

  return (
    <section className="mb-8 font-['Open_Sans']" aria-label="CLOUT reviews">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-['Ovo'] text-2xl text-[#1f1f1f] uppercase">Reviews</h2>
        <div className="text-sm">{renderWriteAction()}</div>
      </div>

      {submittedMessage && (
        <p role="status" className="mb-4 text-sm text-[#1f1f1f] bg-[#f8f6f2] rounded p-3">
          {submittedMessage}
        </p>
      )}

      {writing && (
        <div className="mb-6 border border-[#eaeaea] rounded-lg p-4 bg-white">
          <ReviewForm
            providerName={providerName}
            defaultName={user?.full_name ?? ''}
            onSubmit={handleSubmit}
            onCancel={() => setWriting(false)}
          />
        </div>
      )}

      {loaded && reviews.length === 0 && (
        <p className="text-sm text-[#767472]">No reviews on CLOUT yet.</p>
      )}

      <ul className="space-y-4">
        {reviews.map((review) => (
          <li key={review.id} className="border-b border-[#eaeaea] pb-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <Stars rating={review.rating} />
              <span className="font-semibold text-[#1f1f1f]">{review.reviewer_name}</span>
              <span className="text-xs text-[#767472]">{KIND_LABELS[review.kind]}</span>
              <span className="text-xs text-[#767472]">{formatDate(review.created_at)}</span>
            </div>
            <p className="mt-2 text-sm text-[#1f1f1f] whitespace-pre-wrap">
              {review.review_text}
            </p>
          </li>
        ))}
      </ul>

      {reviews.length < total && (
        <button
          type="button"
          onClick={() => loadPage(reviews.length)}
          className="mt-4 text-sm text-[#c6a664] hover:underline"
        >
          Show more reviews
        </button>
      )}

      {loaded && (
        <p className="mt-4 text-xs text-[#767472]">
          CLOUT checks every review against its review policy before publishing it, and doesn't
          remove reviews for being negative.
        </p>
      )}
    </section>
  );
};

export default ProviderReviews;
